import React from 'react';

interface TableLoadingOverlayProps {
  /**
   * When true the overlay is shown on top of the table, indicating that a
   * page change / search / filter / sort request is in flight.
   */
  active: boolean;
  /** Message shown next to the spinner. Defaults to "Loading". */
  label?: string;
}

/**
 * A translucent loading overlay meant to be placed inside a `tw-relative`
 * container that wraps a data table. It keeps the current rows visible
 * (dimmed) underneath while a backend request is in flight, so the user gets
 * immediate feedback that an operation (page change, search, filter, sort) is
 * being processed instead of the table appearing frozen/unresponsive.
 */
const TableLoadingOverlay: React.FC<TableLoadingOverlayProps> = ({
  active,
  label = 'Loading',
}) => {
  if (!active) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="tw-absolute tw-inset-0 tw-z-40 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-40"
    >
      <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-bg-neutral-800 tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-shadow-lg">
        <span className="tw-w-5 tw-h-5 tw-border-2 tw-border-t-transparent tw-border-white tw-rounded-full tw-animate-spin" />
        <span>{label}…</span>
      </div>
    </div>
  );
};

export default TableLoadingOverlay;
